import React, { useId, useRef, useState } from "react";
import { kitFor } from "../fpl/ShirtKit";

// Scene geometry (percent of the 3:4 scene box).
export const GOAL = { left: 17, width: 66, top: 22, height: 22 };
const SPOT = { left: 50, top: 84 };

export const goalToScene = (x, y) => ({
  left: GOAL.left + ((x + 1) / 2) * GOAL.width,
  top: GOAL.top + GOAL.height - Math.max(-0.1, y) * GOAL.height,
});

const DIVE = {
  L: "translate(-210%, 18%) rotate(-72deg)",
  R: "translate(110%, 18%) rotate(72deg)",
  C: "translate(-50%, -22%)",
};

const Keeper = ({ kit, dive }) => {
  const k = kitFor(kit?.short, kit?.color, true);
  const uid = useId().replace(/:/g, "");
  return (
    <div
      data-testid="keeper-figure"
      className="absolute z-20"
      style={{
        left: "50%",
        top: `${GOAL.top + GOAL.height - 17}%`,
        width: "11%",
        height: "17%",
        transform: dive ? DIVE[dive] : "translate(-50%, 0)",
        transformOrigin: "50% 80%",
        transition: "transform 420ms cubic-bezier(.2,.8,.3,1)",
      }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[-4%] w-[120%] h-[10%] rounded-[50%] bg-black/45 blur-[2px]" />
      <svg viewBox="0 0 60 100" className="relative w-full h-full drop-shadow-[0_6px_6px_rgba(0,0,0,0.45)] keeper-idle">
        <defs>
          <linearGradient id={`kbody${uid}`} x1="0" x2="1">
            <stop offset="0" stopColor={k.body} />
            <stop offset="0.55" stopColor={k.body} />
            <stop offset="1" stopColor="#000" stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id={`kleg${uid}`} x1="0" x2="1">
            <stop offset="0" stopColor="#1b1b1b" />
            <stop offset="1" stopColor="#000" />
          </linearGradient>
        </defs>
        {/* arms up, ready */}
        <path d="M17 26 Q8 24 6 12" stroke={k.sleeve} strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M43 26 Q52 24 54 12" stroke={k.sleeve} strokeWidth="8" strokeLinecap="round" fill="none" />
        <g fill="#f4f4f4" stroke="#111" strokeWidth="1">
          <path d="M1 10 Q1 3 6 3 Q11 3 11 10 L10 15 L2 15 Z" />
          <path d="M49 10 Q49 3 54 3 Q59 3 59 10 L58 15 L50 15 Z" />
        </g>
        <g stroke="#bbb" strokeWidth="0.8"><line x1="4" y1="5" x2="4" y2="10" /><line x1="6.5" y1="4" x2="6.5" y2="10" /><line x1="51.5" y1="4" x2="51.5" y2="10" /><line x1="54" y1="5" x2="54" y2="10" /></g>
        {/* shirt */}
        <path d="M15 24 Q30 17 45 24 L47 57 Q30 60 13 57 Z" fill={`url(#kbody${uid})`} stroke="rgba(0,0,0,.4)" strokeWidth="1" />
        <path d="M25 21 Q30 26 35 21" stroke="rgba(0,0,0,.45)" strokeWidth="2" fill="none" />
        <path d="M15 34 L45 34" stroke={k.sleeve} strokeWidth="2.5" opacity="0.8" />
        <text x="30" y="50" textAnchor="middle" fontSize="11" fontWeight="900" fill={k.sleeve} fontFamily="sans-serif">1</text>
        {/* shorts */}
        <path d="M14 56 L46 56 L45 70 L32 70 L30 64 L28 70 L15 70 Z" fill="#111" />
        {/* legs, socks, boots */}
        <rect x="17" y="69" width="10" height="21" rx="3.5" fill={`url(#kleg${uid})`} />
        <rect x="33" y="69" width="10" height="21" rx="3.5" fill={`url(#kleg${uid})`} />
        <rect x="17" y="76" width="10" height="2.5" fill={k.sleeve} />
        <rect x="33" y="76" width="10" height="2.5" fill={k.sleeve} />
        <path d="M14 90 Q14 87 18 87 L27 87 L28 95 L14 95 Z" fill="#0d0d0d" />
        <path d="M46 90 Q46 87 42 87 L33 87 L32 95 L46 95 Z" fill="#0d0d0d" />
        <path d="M15 94 L28 94 M32 94 L45 94" stroke="#10B981" strokeWidth="1.2" />
        {/* head */}
        <rect x="26.5" y="17" width="7" height="6" fill="#c99168" />
        <circle cx="30" cy="11.5" r="8.5" fill="#e0ac7e" />
        <path d="M21.5 10 Q22 2 30 2 Q38 2 38.5 10 Q35 6 30 6.5 Q25 6 21.5 10 Z" fill="#2a1a10" />
        <circle cx="27" cy="12" r="1" fill="#2a1a10" />
        <circle cx="33" cy="12" r="1" fill="#2a1a10" />
        <path d="M27.5 16 Q30 17.2 32.5 16" stroke="#8a4b2f" strokeWidth="0.9" fill="none" />
      </svg>
    </div>
  );
};

const Ball = ({ target, flying }) => {
  const pos = target ? goalToScene(target.x, target.y) : SPOT;
  return (
    <>
    <div
      aria-hidden="true"
      className="absolute z-[25] rounded-[50%] bg-black/45 blur-[3px]"
      style={{
        left: `${pos.left}%`,
        top: `${target ? GOAL.top + GOAL.height + 1 : SPOT.top + 3.5}%`,
        width: target ? "4%" : "10%",
        height: target ? "1.2%" : "2.6%",
        transform: "translate(-50%, -50%)",
        opacity: target ? 0.35 : 0.8,
        transition: flying ? "left 520ms cubic-bezier(.15,.7,.3,1), top 520ms ease-out, width 520ms, height 520ms, opacity 520ms" : "none",
      }}
    />
    <div
      data-testid="penalty-ball"
      className="absolute z-30"
      style={{
        left: `${pos.left}%`,
        top: `${pos.top}%`,
        width: "11%",
        aspectRatio: "1",
        transform: `translate(-50%, -50%) scale(${target ? 0.4 : 1})`,
        transition: flying ? "left 520ms cubic-bezier(.15,.7,.3,1), top 520ms cubic-bezier(.1,.9,.3,1), transform 520ms ease-out" : "none",
      }}
    >
      <svg viewBox="0 0 40 40" className={`w-full h-full drop-shadow-[0_6px_6px_rgba(0,0,0,.5)] ${flying ? "ball-spin" : ""}`}>
        <circle cx="20" cy="20" r="19" fill="#fff" stroke="#222" strokeWidth="1" />
        <path d="M20 11 L27 16 L24.5 24 L15.5 24 L13 16 Z" fill="#222" />
        <path d="M20 11 L20 2 M27 16 L35 12 M24.5 24 L30 32 M15.5 24 L10 32 M13 16 L5 12" stroke="#222" strokeWidth="1.4" />
        <circle cx="20" cy="20" r="19" fill="url(#bshade)" />
        <defs>
          <radialGradient id="bshade" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.35" />
          </radialGradient>
        </defs>
      </svg>
    </div>
    </>
  );
};

const Crowd = () => (
  <div className="absolute inset-x-0 top-0 h-[17%] overflow-hidden" aria-hidden="true">
    <div
      className="absolute inset-0 opacity-60 blur-[0.6px]"
      style={{
        background:
          "radial-gradient(circle, #10B981 1.2px, transparent 1.6px) 0 0/9px 8px, radial-gradient(circle, #38BDF8 1.2px, transparent 1.6px) 4px 3px/11px 9px, radial-gradient(circle, #EF4444 1.1px, transparent 1.5px) 2px 5px/13px 10px, radial-gradient(circle, #ffffff 1px, transparent 1.4px) 6px 1px/7px 7px, linear-gradient(180deg,#0B1220,#1E293B)",
      }}
    />
    <div className="absolute inset-0 crowd-flicker" style={{ background: "linear-gradient(180deg, rgba(11,18,32,.65), rgba(11,18,32,.1))" }} />
    <div className="absolute -top-6 left-[12%] w-24 h-24 rounded-full bg-white/25 blur-2xl" />
    <div className="absolute -top-6 right-[12%] w-24 h-24 rounded-full bg-white/25 blur-2xl" />
  </div>
);

const Boards = () => (
  <div className="absolute inset-x-0 top-[17%] h-[4%] flex z-[5]" aria-hidden="true">
    {["FOOTIE CLASH", "PENALTIES", "FOOTIE CLASH"].map((t, i) => (
      <div
        key={i}
        className="flex-1 flex items-center justify-center text-[8px] sm:text-[10px] font-extrabold tracking-[0.15em] border-r border-black/20 whitespace-nowrap overflow-hidden"
        style={i % 2 ? { background: "#0F172A", color: "#10B981" } : { background: "#10B981", color: "#0F172A" }}
      >
        {t}
      </div>
    ))}
  </div>
);

const PitchLines = () => (
  <svg viewBox="0 0 100 133.33" preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-[6]" aria-hidden="true">
    <g fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="0.5">
      <line x1="-5" y1="58.7" x2="105" y2="58.7" />
      <polyline points="29,58.7 26,66 74,66 71,58.7" />
      <polyline points="10,58.7 -2,90 102,90 90,58.7" />
      <path d="M38 90 Q50 100 62 90" />
    </g>
    <ellipse cx="50" cy="112" rx="1.6" ry="0.9" fill="rgba(255,255,255,.85)" />
  </svg>
);

const NET = "repeating-linear-gradient(60deg, rgba(255,255,255,.32) 0 0.8px, transparent 0.8px 6px), repeating-linear-gradient(-60deg, rgba(255,255,255,.32) 0 0.8px, transparent 0.8px 6px)";

const Goal = () => (
  <div
    className="absolute z-10"
    style={{ left: `${GOAL.left}%`, width: `${GOAL.width}%`, top: `${GOAL.top}%`, height: `${GOAL.height}%` }}
    aria-hidden="true"
  >
    {/* back net with depth */}
    <div className="absolute inset-[10%_7%_0_7%]" style={{ background: `${NET}, linear-gradient(180deg, rgba(0,0,0,.5), rgba(0,0,0,.22))` }} />
    {/* roof + side nets */}
    <div className="absolute left-[1%] right-[1%] top-[2%] h-[9%]" style={{ background: NET, clipPath: "polygon(0 0,100% 0,94% 100%,6% 100%)", opacity: 0.8 }} />
    <div className="absolute left-[1%] w-[6.5%] top-[2%] bottom-0" style={{ background: `${NET}, linear-gradient(90deg, rgba(0,0,0,.3), rgba(0,0,0,.1))`, clipPath: "polygon(0 0,100% 38%,100% 100%,0 100%)" }} />
    <div className="absolute right-[1%] w-[6.5%] top-[2%] bottom-0" style={{ background: `${NET}, linear-gradient(270deg, rgba(0,0,0,.3), rgba(0,0,0,.1))`, clipPath: "polygon(0 38%,100% 0,100% 100%,0 100%)" }} />
    {/* frame: posts + crossbar with rounded shading */}
    <div className="absolute left-0 right-0 top-0 h-[6px] rounded-full" style={{ background: "linear-gradient(180deg,#ffffff,#d9d9d9 60%,#9a9a9a)", boxShadow: "0 0 14px rgba(255,255,255,.35)" }} />
    <div className="absolute left-0 top-0 bottom-0 w-[6px] rounded-t-full" style={{ background: "linear-gradient(90deg,#9a9a9a,#ffffff 45%,#d0d0d0)" }} />
    <div className="absolute right-0 top-0 bottom-0 w-[6px] rounded-t-full" style={{ background: "linear-gradient(90deg,#d0d0d0,#ffffff 55%,#9a9a9a)" }} />
    {/* goal-line shadow */}
    <div className="absolute -bottom-[6%] left-[-2%] right-[-2%] h-[10%] bg-black/30 blur-[3px] rounded-[50%]" />
  </div>
);

const grass = Array.from({ length: 8 })
  .map((_, i) => {
    const a = 21 + (79 * (i * i)) / 64;
    const b = 21 + (79 * ((i + 1) * (i + 1))) / 64;
    return `${i % 2 ? "#0a8f46" : "#0ca150"} ${a}% ${b}%`;
  })
  .join(",");

// 3D-feel penalty view from behind the ball. Swipes are reported in px relative to the scene.
export const PenaltyScene = ({ keeperKit, keeperDive, ballTarget, ballFlying, mode, onSwipe, banner, caption, children }) => {
  const ref = useRef(null);
  const start = useRef(null);
  const [trail, setTrail] = useState([]);

  const pt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now(), W: r.width, H: r.height };
  };
  const down = (e) => {
    if (!mode) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start.current = pt(e);
    setTrail([start.current]);
  };
  const move = (e) => {
    if (!start.current) return;
    const p = pt(e);
    setTrail((t) => (t.length > 40 ? [...t.slice(1), p] : [...t, p]));
  };
  const up = (e) => {
    if (!start.current) return;
    const a = start.current;
    const b = pt(e);
    start.current = null;
    setTimeout(() => setTrail([]), 250);
    onSwipe && onSwipe({ dx: b.x - a.x, dy: a.y - b.y, ms: Math.max(16, b.t - a.t), W: b.W, H: b.H });
  };

  return (
    <div
      ref={ref}
      data-testid="penalty-scene"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => { start.current = null; setTrail([]); }}
      className={`relative w-full aspect-[3/4] max-h-[72vh] mx-auto rounded-2xl overflow-hidden select-none ${mode ? "cursor-grab" : ""}`}
      style={{ touchAction: "none", background: `linear-gradient(180deg, #0F172A 0 21%, ${grass})` }}
    >
      <Crowd />
      <Boards />
      <PitchLines />
      <Goal />
      <Keeper kit={keeperKit} dive={keeperDive} />
      <Ball target={ballTarget} flying={ballFlying} />
      <div className="absolute inset-x-0 top-[21%] bottom-0 pointer-events-none z-[4] opacity-40" style={{ background: "repeating-linear-gradient(90deg, rgba(255,255,255,.05) 0 2px, transparent 2px 5px), radial-gradient(ellipse at 50% 0%, rgba(255,255,255,.22), transparent 60%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-[30%] pointer-events-none z-[7]" style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(0,0,0,.35), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none z-[8]" style={{ boxShadow: "inset 0 0 60px rgba(0,0,0,.45)" }} />
      {trail.length > 1 && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-40">
          <polyline
            points={trail.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={mode === "save" ? "#38BDF8" : "#10B981"}
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.8"
          />
        </svg>
      )}
      {caption && (
        <div data-testid="scene-caption" className="absolute inset-x-0 bottom-[3%] z-40 text-center pointer-events-none">
          <span className="inline-block rounded-full bg-[#0F172A]/85 border border-white/15 px-4 py-1.5 text-xs sm:text-sm font-semibold text-white backdrop-blur-sm">
            {caption}
          </span>
        </div>
      )}
      {banner && (
        <div data-testid="scene-banner" className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div
            className="banner-pop font-display font-extrabold text-4xl sm:text-5xl px-6 py-2 rounded-xl -rotate-3 shadow-2xl"
            style={banner.good ? { background: "#10B981", color: "#0F172A" } : { background: "#EF4444", color: "#fff" }}
          >
            {banner.text}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default PenaltyScene;
